import { useState } from "react";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  AdminViewToggle,
  useAdminViewMode,
} from "@jambushrushti/components/admin/AdminViewToggle";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Input } from "@jambushrushti/components/ui/input";
import { Skeleton } from "@jambushrushti/components/ui/skeleton";
import {
  Mail,
  Trash2,
  Calendar,
  CheckCircle2,
  Circle,
  Search,
  User,
  Filter,
} from "lucide-react";
import {
  useContactUsSubmissions,
  useUpdateContactUs,
  useDeleteContactUs,
} from "@jambushrushti/hooks/useContactUs";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@jambushrushti/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";

export default function ContactUsManagement() {
  const { data: submissions, isLoading } = useContactUsSubmissions();
  const updateMutation = useUpdateContactUs();
  const deleteMutation = useDeleteContactUs();

  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useAdminViewMode();
  const [filterType, setFilterType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const filteredSubmissions = submissions?.filter((item: any) => {
    const matchesSearch =
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.message?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === "all" || item.inquiry_type === filterType;
    const matchesStatus =
      filterStatus === "all" ||
      item.status === filterStatus ||
      (!item.status && filterStatus === "new");

    return matchesSearch && matchesType && matchesStatus;
  });

  const unreadCount =
    submissions?.filter((f: any) => !f.status || f.status === "new").length ||
    0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-gold">
              <Mail className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground">
                Contact Us Submissions
              </h1>
              <p className="text-muted-foreground">
                {unreadCount > 0
                  ? `${unreadCount} new submissions`
                  : "Manage contact us inquiries"}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-border/50">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search submissions..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-40">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="library">Library Visit</SelectItem>
                  <SelectItem value="research">
                    Research Collaboration
                  </SelectItem>
                  <SelectItem value="technical">Technical Support</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <AdminViewToggle value={viewMode} onChange={setViewMode} />
            </div>
          </CardContent>
        </Card>

        {/* Submissions List */}
        <div
          className={
            viewMode === "cards"
              ? "grid gap-4 md:grid-cols-2 xl:grid-cols-3"
              : "space-y-4"
          }
        >
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : filteredSubmissions?.length === 0 ? (
            <Card className="border-border/50">
              <CardContent className="py-12 text-center">
                <Mail className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
                <h3 className="font-heading mb-1 font-semibold text-foreground">
                  No Submissions Found
                </h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm || filterType !== "all" || filterStatus !== "all"
                    ? "Try adjusting your filters"
                    : "New contact us inquiries will appear here"}
                </p>
              </CardContent>
            </Card>
          ) : viewMode === "table" ? (
            <div className="overflow-x-auto rounded-xl border border-border/60 bg-card md:col-span-2 xl:col-span-3">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="border-b bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Visitor</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Subject & Message</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {filteredSubmissions?.map((item: any) => (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">
                          {item.name || "Anonymous"}
                        </p>
                        <p className="max-w-52 truncate text-xs text-muted-foreground">
                          {item.email || "No email details"}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className="bg-umber-100 capitalize text-umber-700">
                          {item.inquiry_type || "General"}
                        </Badge>
                      </td>
                      <td className="max-w-sm px-4 py-3">
                        <p className="mb-1 line-clamp-1 font-medium text-foreground">
                          {item.subject}
                        </p>
                        <p className="line-clamp-2 text-xs text-muted-foreground">
                          {item.message}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {item.created_at
                          ? format(new Date(item.created_at), "MMM d, yyyy")
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            item.status === "resolved" ? "secondary" : "outline"
                          }
                        >
                          {item.status === "resolved" ? "Resolved" : "New"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              updateMutation.mutate({
                                id: item.id,
                                status:
                                  item.status === "resolved"
                                    ? "new"
                                    : "resolved",
                              })
                            }
                            title={
                              item.status === "resolved"
                                ? "Mark as new"
                                : "Mark as resolved"
                            }
                          >
                            {item.status === "resolved" ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <Circle className="h-4 w-4" />
                            )}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:bg-destructive hover:text-white"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Delete Submission
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this
                                  submission? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteMutation.mutate(item.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            filteredSubmissions?.map((item: any) => (
              <Card
                key={item.id}
                className={`border-border/50 transition-all ${item.status !== "resolved" ? "border-l-4 border-l-primary" : ""}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                    <div className="flex min-w-0 flex-1 items-start gap-4">
                      {/* Avatar/Status */}
                      <div className="shrink-0">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                          {item.name ? (
                            <span className="font-semibold text-primary">
                              {item.name.charAt(0).toUpperCase()}
                            </span>
                          ) : (
                            <User className="h-5 w-5 text-primary" />
                          )}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <span className="font-heading max-w-full truncate font-semibold text-foreground">
                            {item.name || "Anonymous"}
                          </span>
                          <Badge className="shrink-0 bg-umber-100 capitalize text-umber-700">
                            {item.inquiry_type || "General"}
                          </Badge>
                          {item.status !== "resolved" && (
                            <Badge
                              variant="outline"
                              className="shrink-0 border-primary text-primary"
                            >
                              New
                            </Badge>
                          )}
                        </div>

                        {/* Contact Info */}
                        <div className="mb-3 flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:gap-4">
                          {item.email && (
                            <div className="flex min-w-0 items-center gap-1.5">
                              <Mail className="h-3.5 w-3.5 shrink-0" />
                              <a
                                href={`mailto:${item.email}`}
                                className="truncate hover:text-primary"
                                title={item.email}
                              >
                                {item.email}
                              </a>
                            </div>
                          )}
                          <div className="flex shrink-0 items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">
                              {item.created_at
                                ? format(
                                    new Date(item.created_at),
                                    "MMM d, yyyy 'at' h:mm a"
                                  )
                                : "—"}
                            </span>
                          </div>
                        </div>

                        {/* Message */}
                        <div className="mt-3 text-foreground">
                          <h4 className="mb-1 text-sm font-medium">
                            {item.subject}
                          </h4>
                          <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
                            {item.message}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-2 flex w-full shrink-0 items-center justify-end gap-2 self-end border-t border-border/50 pt-3 sm:mt-0 sm:w-auto sm:self-start sm:border-0 sm:pt-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          updateMutation.mutate({
                            id: item.id,
                            status:
                              item.status === "resolved" ? "new" : "resolved",
                          })
                        }
                        title={
                          item.status === "resolved"
                            ? "Mark as new"
                            : "Mark as resolved"
                        }
                      >
                        {item.status === "resolved" ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4" />
                        )}
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:bg-destructive hover:text-white"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Delete Submission
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this submission?
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(item.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
